import { useState } from 'react';
import { CUE_TEMPLATES, hhmmToMinutes, minutesToHHMM } from '@micplot/shared';
import { api } from '../lib/api';
import { useStore, useCues } from '../state/store';
import ConfirmDialog from './ConfirmDialog';

/**
 * Start a running order from a familiar shape.
 *
 * Typing out a set list before every show is the chore that stops people using
 * set-list mode at all. These cover the structures that recur, timed from a
 * curtain you set, and every item is meant to be edited afterwards.
 */
export default function TemplatePicker({ onClose }: { onClose: () => void }) {
  const identity = useStore(s => s.identity)!;
  const notify = useStore(s => s.notify);
  const cues = useCues();

  const [curtain, setCurtain] = useState('19:30');
  const [chosen, setChosen] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const curtainMinutes = hhmmToMinutes(curtain);
  const template = CUE_TEMPLATES.find(t => t.id === chosen);

  async function apply(id: string) {
    if (curtainMinutes === null) {
      notify('Curtain time should look like 19:30.', 'error');
      return;
    }
    try {
      const r = await api.applyCueTemplate(identity.code, id, curtainMinutes);
      notify(`Started from "${r.template}" — ${r.items} items. Edit them to suit the show.`);
      onClose();
    } catch (err) {
      notify((err as Error).message, 'error');
    }
  }

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-label="Start from a template">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2>Start a running order</h2>
          <button className="btn sm" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <p className="sub">
          A starting point, not a house style — rename, reorder and retime anything.
        </p>

        <div className="field">
          <label htmlFor="curtain">Curtain</label>
          <input id="curtain" value={curtain} onChange={e => setCurtain(e.target.value)}
                 placeholder="19:30" style={{ width: 110 }} />
          <div className="hint">
            Everything is timed from here. Items before curtain, like house open,
            sit earlier.
          </div>
        </div>

        <div className="field">
          <label>Shape</label>
          <div className="seg">
            {CUE_TEMPLATES.map(t => (
              <button key={t.id} aria-pressed={chosen === t.id} onClick={() => setChosen(t.id)}>
                <strong>{t.name}</strong>
                <div className="hint">{t.description}</div>
              </button>
            ))}
          </div>
        </div>

        {template && curtainMinutes !== null && (
          <div className="field">
            <label>What you will get</label>
            <div className="tpl-preview">
              {template.items.map(item => {
                const from = Math.max(0, Math.min(1440, curtainMinutes + item.offset));
                return (
                  <div className="tpl-row" key={item.label}>
                    <span className="cue-time">{minutesToHHMM(from)}</span>
                    <span style={{ flex: 1 }}>{item.label}</span>
                    {item.kind !== 'other' && (
                      <span className={`chip kind-${item.kind}`}>{item.kind}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!chosen || curtainMinutes === null}
                  onClick={() => {
                    if (!chosen) return;
                    // Replacing an existing order is worth asking about.
                    if (cues.length > 0) setConfirming(chosen);
                    else void apply(chosen);
                  }}>
            Use this
          </button>
        </div>
      </div>

      {confirming && (
        <ConfirmDialog
          title="Replace the running order?"
          body={`The ${cues.length} item${cues.length === 1 ? '' : 's'} already here will be removed, `
            + 'along with the mic assignments on them.'}
          confirmLabel="Replace it"
          danger
          onConfirm={() => {
            const id = confirming;
            setConfirming(null);
            void apply(id);
          }}
          onCancel={() => setConfirming(null)}
        />
      )}
    </div>
  );
}
